import React from 'react'
import { Image, ImageBase } from '../../utils/image'
import { pageheaderGenericSrc, pageheaderGenericSrcSet } from '../../utils/imageSizes'
import { Link } from '../../utils/link'
import { HomePageUSPs } from '../cmsPage/page/homePage/homePageLayout'
import { QuickUtilitiesMenu, QuickUtilsProps } from '../quickShareMenu/quickUtilitiesMenu'
import { Breadcrumbs } from './breadcrumbs'
import { Readspeaker } from './readspeaker'

// ----- Hero

type HeroPageHeaderProps = {
  title: string
  intro: string
  visual?: JSX.Element
  topTasks?: JSX.Element
} & (
  | {
      kind: 'home'
    }
  | {
      kind: 'centra-home' | 'centra-spaarne' | 'centra-department' | 'centra-subDepartment' | 'friend-spaarne'
      link?: { title: string; url: string }
      breadcrumbs: { url: string; title: string }[]
    }
)

export const HeroPageHeader = (props: HeroPageHeaderProps) => (
  <header
    className={`pageheader--hero pageheader--home${
      props.kind.startsWith('centra') || props.kind === 'friend-spaarne' ? ' pageheader--centra' : ''
    }`}
  >
    {props.visual}

    <div className="pageheader__inner">
      <div className={`${props.kind === 'home' && 'pageheader__home-container'} pageheader__text`}>
        {props.kind !== 'home' && <Breadcrumbs links={props.breadcrumbs} />}
        <Readspeaker />
        <div className={props.kind === 'home' ? 'homepage__usps-container' : ''}>
          <div className={props.kind === 'home' ? 'homepage__usps-intro' : ''}>
            <h1 className="pageheader__title title--xxl">{props.title}</h1>
            <p className="pageheader__intro typo--m">{props.intro}</p>
          </div>
          {props.kind === 'home' && <HomePageUSPs />}
        </div>
        {props.kind !== 'home' && props.link && props.link.url && props.link.title && (
          <Link url={props.link.url} className="pageheader__cta button--primary">
            <span className="button__label">{props.link.title}</span>
          </Link>
        )}
      </div>
      {props.topTasks}
    </div>
  </header>
)

// ----- Generic

export type PageHeaderKind =
  | 'spaarne'
  | 'verpleegafdelingenOverview'
  | 'departmentOrCentrumOverview'
  | 'departmentDetail'
  | 'dossierPage'
  | 'subDepartmentDetail'
  | 'specialistOverview'
  | 'specialistDetail'
  | 'newsOverview'
  | 'newsDetail'
  | 'eventOverview'
  | 'eventDetail'
  | 'newsAndEventsOverview'
  | 'locationDetail'
  | 'researchOverview'
  | 'researchDetail'
  | 'search'
  | 'video'
  | '404'
  | 'folders'
  | 'waitingTimes'

export type PageHeaderProps = {
  kind: PageHeaderKind
  title: string
  intro?: React.ReactNode
  link?: { url: string; title: string }
  image?: ImageBase
  breadcrumbs: { url: string; title: string }[]
  extraJSXBelowTitle?: React.ReactNode
  extraJSXBelowIntro?: React.ReactNode
  extraJSXBelowText?: React.ReactNode
  quickUtils?: QuickUtilsProps
}

export const GenericPageHeader = (props: PageHeaderProps) => {
  const {
    title,
    intro,
    link,
    kind,
    image,
    breadcrumbs,
    extraJSXBelowTitle,
    extraJSXBelowIntro,
    extraJSXBelowText,
    quickUtils,
  } = props
  const pagheaderBorderClasses =
    (kind == 'spaarne' && !extraJSXBelowText) ||
    kind == 'specialistDetail' ||
    kind == 'locationDetail' ||
    kind == 'researchOverview' ||
    kind == 'researchDetail' ||
    kind == 'newsDetail' ||
    kind == 'folders' ||
    kind == 'eventDetail'
      ? ' pageheader--border'
      : ''
  const pageheaderNavClasses = kind === 'spaarne' && extraJSXBelowText ? ' pageheader--has-nav' : ' pageheader--no-nav'
  return (
    <header
      className={`pageheader--default pageheader--${
        image ? 'image' : 'text'
      } container--m gutter--m pageheader--${kind}${pagheaderBorderClasses}${pageheaderNavClasses}`}
    >
      <Breadcrumbs links={breadcrumbs} style={kind === 'dossierPage' ? 'white' : undefined} />
      <Readspeaker />

      <div className="pageheader__inner">
        <div className="pageheader__text">
          {title && <h1 className="pageheader__title title--xxl">{title}</h1>}

          {extraJSXBelowTitle}

          {intro ? (
            typeof intro == 'string' ? (
              <p className="pageheader__intro typo--m" dangerouslySetInnerHTML={{ __html: intro }} />
            ) : (
              <p className="pageheader__intro typo--m">{intro}</p>
            )
          ) : (
            <></>
          )}

          {extraJSXBelowIntro}

          {link && link.url && link.title && (
            <Link url={link.url} className="pageheader__cta button--primary">
              <span className="button__label">{link.title}</span>
            </Link>
          )}

          {quickUtils && <QuickUtilitiesMenu {...quickUtils} />}
        </div>

        {extraJSXBelowText}
      </div>

      {image && (
        <div className="pageheader__visual fit-object">
          <Image
            {...image}
            src={pageheaderGenericSrc}
            srcset={pageheaderGenericSrcSet}
            sizes="(min-width: 81.5rem) 1224px, 100vw"
            lazyload={false}
            classNames={{ image: 'pageheader__image' }}
            width="1224"
            height="574"
          />
        </div>
      )}
    </header>
  )
}

export const GenericPageHeaderWrapper = (p: {kind: PageHeaderKind, children: React.ReactNode}) => {
  const { kind, children } = p
  return kind == 'dossierPage' ? <div className={`pageheader--${kind}`}>{children}</div> : <>{children}</>
}
